/**
 * Creates a GeoJSON circle polygon for visualization on a map
 *
 * @param {number} centerLat - Center latitude in degrees
 * @param {number} centerLng - Center longitude in degrees
 * @param {number} radius - Radius in the specified unit
 * @param {'miles'|'km'} unit - Unit of measurement (default: 'miles')
 * @param {number} points - Number of points to approximate the circle (default: 256)
 * @returns {Object} GeoJSON Feature with Polygon geometry
 */
export function createCircle(centerLat, centerLng, radius, unit = 'miles', points = 256) {
  const coords = [];

  // Earth's radius approximations per degree
  // Miles: ~69.17 miles per degree
  // Kilometers: ~111.32 km per degree
  const unitsPerDegreeLat = unit === 'km' ? 111.32 : 69.17;
  const unitsPerDegreeLng = unitsPerDegreeLat * Math.cos((centerLat * Math.PI) / 180);

  // Convert radius to degrees for lat/lng
  // Cap deltaLng at 180° so the circle fills at most one world width (no wrapping artifacts)
  const deltaLat = radius / unitsPerDegreeLat;
  const deltaLng = Math.min(radius / unitsPerDegreeLng, 180);

  // Generate points around the circle
  for (let i = 0; i <= points; i++) {
    const angle = (i / points) * 2 * Math.PI;
    const lat = Math.max(-85, Math.min(85, centerLat + deltaLat * Math.sin(angle)));
    const lng = centerLng + deltaLng * Math.cos(angle);
    coords.push([lng, lat]); // GeoJSON uses [lng, lat] order
  }

  return {
    type: "Feature",
    geometry: {
      type: "Polygon",
      coordinates: [coords],
    },
    properties: {
      radius,
      unit,
      center: [centerLng, centerLat],
    },
  };
}

/**
 * Computes the geographic bounding box for a circle defined by center + radius.
 * Returns format suitable for Mapbox's fitBounds: [[west, south], [east, north]]
 *
 * @param {number} centerLat - Center latitude in degrees
 * @param {number} centerLng - Center longitude in degrees
 * @param {number} radius - Radius in the specified unit
 * @param {'miles'|'km'} unit - Unit of measurement (default: 'miles')
 * @returns {[[number, number], [number, number]]} LngLatBoundsLike
 */
export function computeRadiusBounds(centerLat, centerLng, radius, unit = 'miles') {
  const unitsPerDegreeLat = unit === 'km' ? 111.32 : 69.17;
  const unitsPerDegreeLng = unitsPerDegreeLat * Math.cos((centerLat * Math.PI) / 180);

  const deltaLat = radius / unitsPerDegreeLat;
  const deltaLng = radius / unitsPerDegreeLng;

  const south = Math.max(-85, centerLat - deltaLat);
  const north = Math.min(85, centerLat + deltaLat);
  const west = Math.max(-180, centerLng - deltaLng);
  const east = Math.min(180, centerLng + deltaLng);

  return [[west, south], [east, north]];
}
